/**
 * Pure classificatie- en contractlaag voor foutmelding. GEEN import van de Sentry-SDK:
 * zo blijft dit toetsbaar zonder browser-runtime, en kan de regel later ook
 * server-side hergebruikt worden zonder een tweede definitie.
 *
 * Zie `observability.ts` voor het verzendpad en `@collectr/observability` voor de maskering.
 */
/**
 * ALLOW-LIST. Uitsluitend deze sleutels verlaten de applicatie. Alles wat vrije
 * tekst kan dragen — namen, adressen, formulierinhoud, dossiernotities,
 * documentinhoud, tokens, volledige request bodies — hoort er per definitie niet in.
 */
export type ObservabilityContext = {
  /** Waar het misging, bv. "dossier-intake-scherm" of "login". */
  component: string;
  /** Korte aanduiding van de handeling, bv. "submit_intake". */
  operation?: string;
  tenantId?: string | null;
  userId?: string | null;
  dossierId?: string | null;
  /** Correlatie-id van het verzoek, wanneer de server er een teruggaf. */
  requestId?: string | null;
  /** Machineleesbare foutcode, bv. "validation_error" of "session_not_found". */
  code?: string | null;
  /** HTTP-status wanneer de fout van een server kwam. */
  httpStatus?: number | null;
};

export const TOEGESTANE_SLEUTELS = [
  "component",
  "operation",
  "tenantId",
  "userId",
  "dossierId",
  "requestId",
  "code",
  "httpStatus",
] as const;

export type Ernst = "error" | "warning";

/**
 * Drie klassen, zoals afgesproken:
 *  - `business`  verwachte validatie of autorisatie → NIET melden (alarmmoeheid)
 *  - `security`  operationeel relevante auth-afwijking → warning
 *  - `technical` onverwacht falen → error
 */
export type Foutklasse = "business" | "security" | "technical" | "operational";

/** Codes die een NORMALE afwijzing zijn: de gebruiker deed iets ongeldigs, het systeem werkt. */
const ZAKELIJKE_CODES = new Set([
  "validation_error",
  "payload_invalid",
  "invalid_json",
  "dossier_reference_collision",
  "invalid_intake_status",
  "intake_item_not_found",
  "debtor_not_found",
  "creditor_missing",
  "invalid_principal",
  "invalid_segment",
  "trusted_source_required",
  "promotion_outcome_unknown",
]);

/**
 * Operationele afwijkingen: geen fout in de code en geen aanval, maar gedrag dat je
 * wilt zien. Komen binnen als WAARSCHUWING zodat ze niet als storing meetellen.
 */
const OPERATIONELE_CODES = new Set(["request_loop_detected"]);

/** Codes die een auth-afwijking zijn: geen storing, wél het volgen waard. */
const BEVEILIGINGSCODES = new Set([
  "account_inactive",
  "forbidden_role",
  "auth_session_invalid",
  "auth_header_missing",
  "user_profile_missing",
  "tenant_missing",
  "session_not_found",
  "mfa_verification_failed",
  "invalid_credentials",
  "interne_rol",
  "42501",
]);

/**
 * Bepaalt de klasse uit foutcode en HTTP-status.
 *
 * De statusregel is bewust ondergeschikt aan de code: een 400 met een bekende
 * zakelijke code is zakelijk, maar een 400 zonder code kan van alles zijn en is
 * dus niet automatisch onschuldig. 5xx is altijd technisch.
 */
export function classificeerFout(args: { code?: string | null; httpStatus?: number | null }): Foutklasse {
  const code = (args.code ?? "").trim();
  const status = args.httpStatus ?? null;

  if (code && OPERATIONELE_CODES.has(code)) return "operational";
  if (status !== null && status >= 500) return "technical";
  if (code && ZAKELIJKE_CODES.has(code)) return "business";
  if (code && BEVEILIGINGSCODES.has(code)) return "security";
  if (status === 401 || status === 403) return "security";
  if (status !== null && status >= 400 && status < 500) return "business";
  return "technical";
}
